import React, { useMemo, useState } from 'react';
import { Minus, Plus, Search } from 'lucide-react';
import ProductGallery from './ProductGallery';
import DishNutrition from './DishNutrition';
import './mealPack.css';

export const selectionCount = (selections) => selections.reduce((sum, pick) => sum + pick.quantity, 0);
export const storeDefaults = (items, size, defaultIds = []) => {
  const allowed = new Set(items.map(item => item.item_id));
  const ids = [...new Set(defaultIds)].filter(id => allowed.has(id));
  const pool = ids.length ? ids : [...allowed];
  if (!pool.length) return [];
  const counts = {};
  for (let i = 0; i < size; i += 1) counts[pool[i % pool.length]] = (counts[pool[i % pool.length]] || 0) + 1;
  return Object.entries(counts).map(([item_id, quantity]) => ({ item_id, quantity }));
};

/** Shared by the storefront and portal: the supplied menu is for ONE delivery. */
export default function MealSelectionGrid({ items, selections, onChange, size, disabled = false, detailsHref, priceLabel }) {
  const [search, setSearch] = useState('');
  const total = selectionCount(selections);
  const counts = useMemo(() => Object.fromEntries(selections.map(pick => [pick.item_id, pick.quantity])), [selections]);
  const visible = items.filter(item => `${item.name} ${item.description}`.toLowerCase().includes(search.toLowerCase()));
  const change = (id, delta) => {
    if (disabled || (delta > 0 && total >= size)) return;
    const next = { ...counts, [id]: Math.max(0, (counts[id] || 0) + delta) };
    onChange(Object.entries(next).filter(([, quantity]) => quantity > 0).map(([item_id, quantity]) => ({ ...selections.find(p => p.item_id === item_id), item_id, quantity })));
  };
  const toggleOption = (item, group, option, removal, checked) => {
    const field = removal ? 'removed_ingredients' : 'addons';
    onChange(selections.map(p => {
      if (p.item_id !== item.item_id) return p;
      const choices = (p[field] || []).filter(a => a.group !== group.key || a.option !== option.key);
      if (checked && !removal && group.mode !== 'ingredients' && group.max_select != null) {
        if (Number(group.max_select) === 1) {
          return { ...p, [field]: [...choices.filter(a => a.group !== group.key), { group: group.key, option: option.key }] };
        }
        if (choices.filter(a => a.group === group.key).length >= Number(group.max_select)) return p;
      }
      return { ...p, [field]: checked ? [...choices, { group: group.key, option: option.key }] : choices };
    }));
  };
  return <section aria-label="Choose your meals" className="space-y-5">
    <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <Search size={18} className="text-slate-400" />
      <input aria-label="Search this delivery's menu" value={search} onChange={e => setSearch(e.target.value)} placeholder="Find a dish…" className="min-w-0 w-full bg-transparent text-sm outline-none" />
    </label>
    {visible.length === 0 && <p className="rounded-xl border border-dashed p-8 text-center text-slate-500">{items.length ? 'No dishes match your search.' : 'The menu for this delivery is not available yet.'}</p>}
    <div className="mp-dish-grid">
      {visible.map(item => <article key={item.item_id} className={`mp-dish ${counts[item.item_id] ? 'mp-dish-selected' : ''}`}>
        <ProductGallery item={item} />
        <div className="p-4">
          <h3 className="font-semibold text-slate-900">{item.name}</h3>
          {priceLabel && <p className="mt-2 font-semibold">{priceLabel(item)}</p>}
          <DishNutrition item={item} />
          {counts[item.item_id] > 0 && item.size_options?.length > 1 && <label className="mt-3 block text-sm">Portion for {item.name}
            <select className="mt-1 w-full rounded border p-2" disabled={disabled}
              value={selections.find(p => p.item_id === item.item_id)?.size || ''}
              onChange={e => onChange(selections.map(p => p.item_id === item.item_id ? { ...p, size: e.target.value || null } : p))}>
              <option value="">Regular portion</option>
              {item.size_options.filter(s => !s.is_default).map(s => <option key={s.key} value={s.key}>{s.label} (+{Number(s.price_delta).toFixed(2)})</option>)}
            </select>
          </label>}
          {detailsHref && <a href={detailsHref(item)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs font-medium text-teal-700 underline underline-offset-4">Nutrition & meal details ↗</a>}
          {item.description && <p className="mt-1 text-sm text-slate-500 line-clamp-2">{item.description.replace(/<[^>]*>/g, '')}</p>}
          {Array.isArray(item.dietary_tags) && item.dietary_tags.length > 0 && <p className="mt-2 text-xs text-teal-700">{item.dietary_tags.join(' · ')}</p>}
          {Array.isArray(item.allergens) && item.allergens.length > 0 && <p className="mt-2 text-xs text-slate-500">Contains: {item.allergens.join(', ')}</p>}
          <div className="mt-4 flex items-center justify-end gap-3">
            <button type="button" aria-label={`Remove one ${item.name}`} disabled={disabled || !counts[item.item_id]} onClick={() => change(item.item_id, -1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 disabled:opacity-30"><Minus size={16} /></button>
            <span aria-label={`${counts[item.item_id] || 0} ${item.name} selected`} className="min-w-[20px] text-center font-semibold">{counts[item.item_id] || 0}</span>
            <button type="button" aria-label={`Add one ${item.name}`} disabled={disabled || total >= size} onClick={() => change(item.item_id, 1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-700 text-white disabled:opacity-30"><Plus size={16} /></button>
          </div>
          {counts[item.item_id] > 0 && (item.addon_groups || []).length > 0 && <div className="mt-4 space-y-3">
            <p className="text-xs text-slate-500">Options apply to all {counts[item.item_id]} portions of this dish. Prices are per portion.</p>
            {item.addon_groups.map(group => <fieldset key={group.key} className="space-y-2 border-t pt-2">
              <legend className="text-sm font-semibold">{group.label}</legend>
              {group.min_select > 0 && <p className="text-xs">Choose at least {group.min_select}{group.max_select ? `, up to ${group.max_select}` : ''}.</p>}
              {group.options.map(option => {
                const removal = group.mode === 'ingredients' && !option.price;
                const pick = selections.find(p => p.item_id === item.item_id);
                const checked = (pick?.[removal ? 'removed_ingredients' : 'addons'] || []).some(a => a.group === group.key && a.option === option.key);
                const limitReached = !removal && group.mode !== 'ingredients' && group.max_select != null &&
                  Number(group.max_select) !== 1 && (pick?.addons || []).filter(a => a.group === group.key).length >= Number(group.max_select);
                return <label key={option.key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" disabled={disabled || (!checked && (limitReached || (!removal && option.out_of_stock)))} checked={checked}
                    onChange={e => toggleOption(item, group, option, removal, e.target.checked)} />
                  {removal ? 'Remove ' : ''}{option.label}{option.price > 0 ? ` (+${Number(option.price).toFixed(2)})` : ' (free)'}{option.out_of_stock && !removal ? ' — unavailable' : ''}
                </label>;
              })}
            </fieldset>)}
          </div>}
        </div>
      </article>)}
    </div>
  </section>;
}
